<template>
  <form class="tenant-form">
    <b-loading :active.sync="isLoading" :is-full-page="true"></b-loading>

    <div v-if="!isEdit" class="title">Create a new tenant</div>
    <div v-if="isEdit" class="title">Edit this tenant</div>

    <b-field horizontal label="Unique Tenant Code:">
      <b-input
          v-model="code"
          placeholder="Ex: elPro, prod"
          type="text"
      ></b-input>
    </b-field>
    <b-field horizontal>
      <b-message class="message" type="is-warning">
        <div>Short code (less than 10 chars recommended, 15 max), used in each Authentication Header.</div>
        <div>Should be unique, take care! "_" are forbidden!</div>
      </b-message>
    </b-field>

    <hr/>

    <div class="title">Client facing</div>

    <b-field horizontal label="Title:">
      <b-input
          v-model="title"
          placeholder="Ex: Geogaming, elPro, Corporate Challenge"
          type="text"></b-input>
    </b-field>

    <b-field horizontal label="Provider:">
      <b-input
          v-model="provider"
          placeholder="Ex: Geogaming Pty Ltd, Corporate Challenge"
          type="text"></b-input>
    </b-field>
    <b-field horizontal>
      <b-message class="message" type="is-warning">
        <div>This will be displayed in the footer</div>
      </b-message>
    </b-field>

    <b-field horizontal label="Logo:">
      <div style="position: relative">
        <picture-input
            id="pictureInput"
            ref="tenantLogo"
            :customStrings="fileUploadCustomTexts"
            :removable="true"
            buttonClass="button is-primary is-outlined"
            height="150"
            removeButtonClass="button is-outlined"
            width="250"
            @change="onPictureSelected"
        />
        <div v-if="logoPreviewUrl" id="injectedPreview" style="background-color: white">
          <img id="injectedPreviewImage" v-bind:src="logoPreviewUrl"/>
        </div>
        <button v-if="logoPreviewUrl" class="button is-info is-outlined"
                style="width: 250px; margin-top: 5px;"
                @click.prevent="hideImagePreview">Change logo
        </button>
      </div>
    </b-field>
    <b-field horizontal>
      <b-message class="message" type="is-warning">
        No compression or resize is done, please put an acceptable size for both player and
        admin.
      </b-message>
    </b-field>

    <hr/>

    <div class="title">Technical configuration</div>

    <b-field horizontal label="Email domain restriction:">
      <b-input
          v-model="emailDomain"
          placeholder="Ex: condorcet.com.au"
          type="text"></b-input>
    </b-field>
    <b-field horizontal>
      <b-message class="message" type="is-warning">
        <div>Allow only emails with this domain to connect on the player tenant pager</div>
      </b-message>
    </b-field>

    <b-field horizontal label="Billing JSON:">
      <b-input
          :value="billing"
          placeholder=""
          rows="6"
          type="textarea"
          @input="updateMarkdownPreview"></b-input>
    </b-field>
    <b-field horizontal>
      <b-message class="message" type="is-warning">
        <div>Crazy complicated json, sorry :(</div>
        <br/>
        <div>Ex from elPro:</div>
        <pre>
{
  "team_prices": [
    { "low": 0, "high": 5, "price": 210, "extraTeam": 0 },
    { "low": 6, "high": 10, "price": 420, "extraTeam": 0 },
    { "low": 11, "high": 1000, "price": 420, "extraTeam": 50 }
  ],
  "duration_prices": [
    { "low": 0, "high": 240, "extraHour": 0 },
    { "low": 241, "high": 1000, "extraHour": 100 }
  ]
}
        </pre>
      </b-message>
    </b-field>

    <b-field horizontal label="Expert mode:">
      <b-radio
          v-for="item of expertModeValues" :key="item.code"
          v-model="expertMode"
          :native-value="item.code"
          type="is-link"
      >
        <div class="column field is-horizontal">
          {{ item.label }}
        </div>
      </b-radio>

    </b-field>
    <b-field horizontal>
      <b-message class="messageX" type="is-warning">
        Activate expert mode in Admin
      </b-message>
    </b-field>

    <div class="control submit">
      <button class="button is-light btn-margin" @click.prevent="goToPreviousPage()">Back to tenants list
      </button>
      <button v-if="!isEdit" :disabled="isLoading" class="button is-link" @click.prevent="addTenant">Create
        a new tenant
      </button>
      <button v-if="isEdit" :disabled="isLoading" class="button is-link" @click.prevent="updateTenant">
        Update this tenant
      </button>
    </div>
  </form>
</template>

<script>
import debounce from 'lodash.debounce'
import tenantsService from '../services/tenantsService'
import PictureInput from 'vue-picture-input'
import displayHelper from '../helpers/display-helper'
import imageHelper from '../helpers/image-helper'

export default {
  name: 'tenant-form',
  components: {
    PictureInput
  },
  created() {
    if (this.$route.params.id) {
      this.isEdit = true
      this.tenantId = this.$route.params.id
      this.loadTenantById(this.tenantId)
    }
  },
  data() {
    return {
      expertModeValues: [
        {
          code: false,
          label: 'No, forget about this',
        },
        {
          code: true,
          label: 'Yes, no worries, I\'m a ninja!',
        }
      ],
      fileUploadCustomTexts: {
        upload: '<h1>Bummer!</h1>',
        drag: 'Drop file here or click here to upload'
      },
      isLoading: false,
      logoPreviewUrl: '',

      // for update
      isEdit: false,
      tenantLoaded: null,
      tenantId: null,

      // standard form
      title: '',
      code: '',
      logoUrl: null,
      tenantLogo: null,
      billing: '',
      provider: 'Geogaming Pty Ltd',
      expertMode: false,
      introText: null,
      emailDomain: null,

      createMode: false,

      tenant: '',
    }
  },
  methods: {
    loadTenantById: function (tenantId) {
      this.isLoading = true
      tenantsService.getTenantById(tenantId)
          .then((tenant) => {
            console.log('Tenant received: ' + tenantId)

            this.tenantLoaded = tenant
            this.tenantId = this.tenantLoaded.tenantId
            this.title = this.tenantLoaded.title
            this.code = this.tenantLoaded.code
            this.logoUrl = this.tenantLoaded.logoUrl
            this.billing = this.tenantLoaded.billing
            this.provider = this.tenantLoaded.provider
            this.expertMode = this.tenantLoaded.expertMode
            this.introText = this.tenantLoaded.introText
            this.emailDomain = this.tenantLoaded.emailDomain

            this.injectLogoPreview(this.logoUrl)

            this.isLoading = false
          })
          .catch((err) => {
            displayHelper.displayError(this, 'Generic error: ' + err.message)
            this.isLoading = false
          })
    },
    updateMarkdownPreview: debounce(function (e) {
      this.billing = e
    }, 300),
    injectLogoPreview(logoUrl) {
      this.logoPreviewUrl = logoUrl
    },
    hideImagePreview() {
      this.logoPreviewUrl = ''
    },
    onPictureSelected(tenantLogo) {
      if (tenantLogo) {
        imageHelper.compressImg(this.$refs.tenantLogo.image)
            .then((compressedImgDataUrl) => {
              this.tenantLogo = compressedImgDataUrl
            })
      }
    },
    addTenant() {
      this.isLoading = true
      const json = {
        title: this.title,
        code: this.code ? this.code.replace(/_/gi, '-') : this.code,
        tenantLogo: this.tenantLogo,
        billing: this.billing,
        provider: this.provider,
        expertMode: this.expertMode,
        introText: this.introText,
        emailDomain: this.emailDomain,
      }
      tenantsService.addNewTenant(json, this.image)
          .then(() => {
            this.goToPreviousPage()
          }).catch((error) => {
        displayHelper.displayError(this, 'Create tenant error: ' + error.message)
      })
          .finally(() => {
            this.isLoading = false
          })
      this.createMode = false
    },
    updateTenant() {
      this.isLoading = true
      const json = {
        tenantId: this.tenantId,
        title: this.title,
        code: this.code ? this.code.replace(/_/gi, '-') : this.code,
        logoUrl: this.logoUrl,
        tenantLogo: this.tenantLogo,
        billing: this.billing,
        provider: this.provider,
        expertMode: this.expertMode,
        introText: this.introText,
        emailDomain: this.emailDomain,
      }
      tenantsService.updateTenant(this.tenantId, json)
          .then(() => {
            this.goToPreviousPage()
          }).catch((error) => {
        displayHelper.displayError(this, 'Update tenant error: id=' + this.tenantId + ' >>' + error.message)
      })
          .finally(() => {
            this.isLoading = false
          })
      this.createMode = false
    },
    goToPreviousPage() {
      this.$router.push({name: 'Tenants'})
    },
  },
}
</script>

<style scoped>
.tenant .is-horizontal .field-label {
  flex-grow: 0;
}

.tenant label {
  margin-bottom: 0;
  width: 180px !important;
}

.tenant .column .field {
  align-items: center;
}

.field-label {
  flex-grow: 0;
}

.field-label label {
  width: 180px !important;
}

.picture-input /deep/ .preview-container {
  margin: 0;
  z-index: 1 !important;
}

.picture-input /deep/ .picture-preview {
  margin: 0;
  z-index: 1 !important;
}

.picture-input /deep/ .picture-inner {
  z-index: 1 !important;
}

.picture-input {
  text-align: left !important;
}

.picture-input /deep/ .preview-container {
  margin: 0;
}

#pictureInput, #injectedPreview {
  height: 150px;
  width: 250px;
  position: absolute;
}

#injectedPreviewImage {
  height: 150px;
  width: 250px;
}

#injectedPreview {
  top: 0;
  z-index: 10;
}

.message {
  margin-top: 10px;
  margin-bottom: 30px;
}
</style>
